import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Loader2, ListChecks, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Landing page for the "Línea de Tiempo" sidebar menu entry.
 * 
 * Behavior:
 *  - Loads all services flagged with `mostrar_en_timeline = true`
 *  - If at least one exists: redirects to that service's timeline
 *    (the existing /servicios/:id/timeline page, which already has the
 *    searchable selector so the user can navigate between them).
 *  - If none exist: shows an empty state with instructions on how to mark
 *    services so they appear in the timeline selector.
 */
const LineaTiempoIndex = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [hasServicios, setHasServicios] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const findFirstMarked = async () => {
            try {
                const all = [];
                let page = 1;
                const pageSize = 500;
                for (let i = 0; i < 10; i++) {
                    const res = await axios.get(`${API_URL}/api/servicios`, {
                        params: { page, page_size: pageSize, mostrar_en_timeline: true },
                    });
                    const items = res.data.items || res.data || [];
                    if (items.length === 0) break;
                    all.push(...items);
                    if (items.length < pageSize) break;
                    page += 1;
                }

                const marcados = all;

                if (cancelled) return;

                if (marcados.length > 0) {
                    // Deduplicate by grupo_recurrente_id (keep most recent period)
                    const byKey = new Map();
                    for (const s of marcados) {
                        const key = s.grupo_recurrente_id ? `g:${s.grupo_recurrente_id}` : `i:${s.id}`;
                        const existing = byKey.get(key);
                        if (!existing) {
                            byKey.set(key, s);
                        } else {
                            const newest =
                                (s.periodo_anio || 0) * 100 + (s.periodo_mes || 0) >
                                (existing.periodo_anio || 0) * 100 + (existing.periodo_mes || 0)
                                    ? s : existing;
                            byKey.set(key, newest);
                        }
                    }
                    // Sort alphabetically and pick first
                    const sorted = Array.from(byKey.values()).sort((a, b) =>
                        (a.cliente_nombre || '').localeCompare(b.cliente_nombre || '', 'es')
                    );
                    const target = sorted[0];
                    navigate(`/servicios/${target.id}/timeline`, { replace: true });
                    // Keep loading=true while navigation unmounts this component
                    // so the empty state never flashes for a millisecond.
                    return;
                }

                setHasServicios(false);
                setLoading(false);
            } catch (e) {
                console.error('Error loading services for timeline index:', e);
                if (!cancelled) {
                    setHasServicios(false);
                    setLoading(false);
                }
            }
        };
        findFirstMarked();
        return () => { cancelled = true; };
    }, [navigate]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-3 text-slate-500">
                    <Loader2 className="w-7 h-7 animate-spin text-teal-600" />
                    <p className="text-sm">Cargando línea de tiempo…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                    <Calendar size={20} className="text-teal-700" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Línea de Tiempo</h1>
                    <p className="text-sm text-slate-500">Vista anual del estado de tus servicios</p>
                </div>
            </div>

            <Card className="max-w-2xl">
                <CardContent className="p-8">
                    <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
                            <ListChecks size={28} className="text-amber-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-slate-900 mb-2">
                            Aún no marcaste ningún servicio
                        </h2>
                        <p className="text-slate-600 text-sm max-w-md mb-6">
                            Para visualizar la línea de tiempo de un servicio, primero debes activar el
                            interruptor{' '}
                            <strong className="text-slate-800">"Mostrar en Línea de Tiempo"</strong>{' '}
                            dentro de la ficha del servicio que quieras seguir.
                        </p>

                        <div className="w-full bg-slate-50 border border-slate-200 rounded-lg p-4 text-left text-sm space-y-2 mb-6">
                            <p className="font-semibold text-slate-800">¿Cómo lo activo?</p>
                            <ol className="list-decimal list-inside space-y-1 text-slate-600">
                                <li>Entra a <Link to="/servicios" className="text-teal-700 hover:underline font-medium">Servicios</Link>.</li>
                                <li>Abre el servicio que quieres seguir.</li>
                                <li>En la tarjeta <em>Información del Servicio</em>, activa el switch
                                    <span className="inline-block ml-1 px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 text-xs font-medium">Mostrar en Línea de Tiempo</span>.
                                </li>
                                <li>Vuelve a esta sección y el servicio aparecerá aquí.</li>
                            </ol>
                        </div>

                        <Button asChild className="bg-teal-700 hover:bg-teal-800 text-white">
                            <Link to="/servicios">
                                Ir a Servicios
                                <ArrowRight size={14} className="ml-2" />
                            </Link>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default LineaTiempoIndex;
